<script setup>
import {
  ExportOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons-vue";
import { message, Modal } from "ant-design-vue";
import { h, onMounted, reactive, ref, unref } from "vue";
import { exportFile, getRequest, postRequest } from "@/utils/request.js";
import CompanyInfoModal from "@/views/company/modules/CompanyInfoModal.vue";
import CompanyAuditFormModal from "@/views/company/audit/modules/CompanyAuditFormModal.vue";

const formRef = ref();
const searchFormModel = ref({});
const datasource = ref([]);
const loading = ref(false);
const modalRef = ref();

const columns = [
  {
    key: "type",
    dataIndex: "type",
    title: "类型",
    width: "5%",
  },
  {
    key: "name",
    dataIndex: "name",
    title: "公司/个人名称",
    width: "15%",
  },
  {
    key: "logo",
    dataIndex: "logo",
    title: "LOGO",
    width: "10%",
  },
  {
    key: "authenticationStatus",
    dataIndex: "authenticationStatus",
    title: "认证状态",
    width: "10%",
  },
  {
    key: "code",
    dataIndex: "code",
    title: "统一社会信用代码/身份证号",
    width: "15%",
  },
  {
    key: "createTime",
    dataIndex: "createTime",
    title: "注册时间",
    width: "15%",
  },
  {
    key: "status",
    dataIndex: "status",
    title: "启用状态",
    width: "15%",
  },
  {
    key: "action",
    dataIndex: "action",
    title: "操作",
    width: "300px",
  },
];

const pagination = reactive({
  current: 1,
  pageSize: 10,
  pageSizeOptions: ["10", "50", "100"],
  showTotal: (total, range) => {
    return range[0] + "-" + range[1] + " 共" + total + "条";
  },
  showQuickJumper: true,
  showSizeChanger: true,
  total: 0,
});

const loadData = () => {
  loading.value = true;

  const { current, pageSize } = unref(pagination);
  const requestBody = Object.assign({}, unref(searchFormModel), {
    pageNo: current,
    pageSize,
  });
  getRequest("/company", requestBody)
    .then((response) => {
      if (response.success) {
        pagination.total = response.data.total;
        datasource.value = response.data.records;
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetForm = () => {
  formRef.value.resetFields();
  loadData();
};

const handlePreview = (record) => {
  modalRef.value.title = record.name;
  modalRef.value.handleShow(record);
};

const handleOpen = (record) => {
  Modal.confirm({
    title: `确定要将【${record.name}】停用吗？`,
    content: `确定要将【${record.name}】停用吗？`,
    onOk() {
      postRequest(`/company/open/${record.id}`).then((response) => {
        if (response.success) {
          loadData();
        } else {
          message.error(response.message);
        }
      });
    },
    onCancel() {},
  });
};
const handleClose = (record) => {
  Modal.confirm({
    title: `确定要将【${record.name}】启用吗？`,
    content: `确定要将【${record.name}】启用吗？`,
    onOk() {
      postRequest(`/company/close/${record.id}`).then((response) => {
        if (response.success) {
          loadData();
        } else {
          message.error(response.message);
        }
      });
    },
  });
};
const tableChange = ({ current, pageSize }) => {
  pagination.current = current;
  pagination.pageSize = pageSize;
  loadData();
};

const exporting = ref(false);
const exportExcel = () => {
  const requestBody = Object.assign({}, unref(searchFormModel));
  exporting.value = true;
  exportFile("/company/export", requestBody, "企业导出.xlsx").finally(() => {
    exporting.value = false;
  });
};

onMounted(() => {
  loadData();
});
</script>

<template>
  <div>
    <a-card>
      <a-form
        ref="formRef"
        layout="inline"
        :labelCol="{ span: 8, offset: 1 }"
        :model="searchFormModel"
        @keydown.enter="loadData"
      >
        <a-row :gutter="[10, 10]" style="width: 100%">
          <a-col :span="6">
            <a-form-item label="公司/个人名称" name="name">
              <a-input
                v-model:value.trim="searchFormModel.name"
                placeholder="请输入公司/个人名称"
              />
            </a-form-item>
          </a-col>
          <a-col :span="6">
            <a-form-item label="统一社会信用代码/身份证号" name="code">
              <a-input
                v-model:value.trim="searchFormModel.code"
                placeholder="请输入统一社会信用代码/身份证号"
              />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-button
              type="primary"
              style="margin-left: 18px"
              :icon="h(SearchOutlined)"
              @click="loadData"
              @keydown.enter="loadData"
            >
              查询
            </a-button>
            <a-button
              style="margin-left: 18px"
              :icon="h(ReloadOutlined)"
              @click="resetForm"
              >重置
            </a-button>
          </a-col>
        </a-row>
      </a-form>
    </a-card>

    <a-card style="margin-top: 10px">
      <div style="margin-bottom: 12px">
        <a-button
          type="primary"
          @click="exportExcel"
          :icon="h(ExportOutlined)"
          :loading="exporting"
        >
          导出
        </a-button>
      </div>
      <a-table
        row-key="id"
        :loading="loading"
        :columns="columns"
        :data-source="datasource"
        :bordered="true"
        :pagination="pagination"
        @change="tableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'logo'">
            <a-avatar :size="80" :src="$imageUrl(record[column.dataIndex])" />
          </template>
          <template v-if="column.dataIndex === 'authenticationStatus'">
            <template v-if="record[column.dataIndex] === 'DONE'">
              <a-tag color="green">已认证</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'DOING'">
              <a-tag color="gray">待审核</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'NOT_DONE'">
              <a-tag color="orange">未认证</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'REJECT'">
              <a-tag color="red">审核驳回</a-tag>
            </template>
            <template v-else>{{ record[column.dataIndex] }}</template>
          </template>

          <template v-if="column.dataIndex === 'code'">
            <template v-if="record['type'] === 'COMPANY'">
              {{ record["code"] }}
            </template>
            <template v-else-if="record['type'] === 'PERSON'">
              {{ record["idCard"] }}
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'type'">
            <template v-if="record[column.dataIndex] === 'COMPANY'">
              <a-tag color="green">公司</a-tag>
            </template>
            <template v-else-if="record[column.dataIndex] === 'PERSON'">
              <a-tag color="green">个人</a-tag>
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>

          <template v-if="column.dataIndex === 'status'">
            <template v-if="record[column.dataIndex]">
              <a-tag color="green">启用</a-tag>
            </template>
            <template v-else>
              <a-tag color="red">停用</a-tag>
            </template>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <a type="link" @click="handlePreview(record)">查看</a>
            <a-divider type="vertical" />
            <template v-if="record['status']">
              <a type="link" @click="handleClose(record)">停用</a>
            </template>
            <template v-else>
              <a type="link" @click="handleOpen(record)">启用</a>
            </template>
          </template>
        </template>
      </a-table>
    </a-card>
    <company-info-modal ref="modalRef" />
  </div>
</template>

<style scoped lang="scss"></style>
